// EntryTypesCard.jsx
import React from 'react';

/**
 * Props:
 * - types: [{ id, name, subtypes: [{ id, name }] }]
 * - entryTypeIds, entrySubtypeIds: number[]
 * - profileTypeIds, profileSubtypeIds: number[]
 * - useProfileDefaults: boolean
 * - onToggleUseDefaults(checked)
 * - onChangeTypes(nextIds:number[])
 * - onChangeSubtypes(nextIds:number[])
 */
export default function EntryTypesCard({
  types = [],
  entryTypeIds = [],
  entrySubtypeIds = [],
  profileTypeIds = [],
  profileSubtypeIds = [],
  useProfileDefaults = true,
  onToggleUseDefaults,
  onChangeTypes,
  onChangeSubtypes,
}) {
  const [open, setOpen] = React.useState(false);
  const [snap, setSnap] = React.useState(null);

  const using = useProfileDefaults
    ? { typeIds: profileTypeIds, subIds: profileSubtypeIds }
    : { typeIds: entryTypeIds, subIds: entrySubtypeIds };

  const chips = summarize(types, using.typeIds, using.subIds);

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <div className="title">Types &amp; Subtypes</div>
          <div className="sub">Control who this entry is meant for.</div>
        </div>

        <label className="switch">
          <input
            type="checkbox"
            checked={!!useProfileDefaults}
            onChange={(e) => onToggleUseDefaults?.(e.target.checked)}
          />
          <span>Use my profile defaults</span>
        </label>
      </div>

      <div className="chips">{chips}</div>

      <div className="actions">
        <button
          type="button"
          className="btn"
          onClick={() => {
            setSnap({ types: [...entryTypeIds], subs: [...entrySubtypeIds] });
            setOpen(true);
          }}
          disabled={!!useProfileDefaults}
        >
          Customize…
        </button>

        {!useProfileDefaults && (
          <button
            type="button"
            className="link"
            onClick={() => {
              onChangeTypes?.([]);
              onChangeSubtypes?.([]);
            }}
          >
            Clear
          </button>
        )}
      </div>

      {open && (
        <FullListModal
          types={types}
          typeIds={entryTypeIds}
          subIds={entrySubtypeIds}
          onChangeTypes={onChangeTypes}
          onChangeSubtypes={onChangeSubtypes}
          onClose={(cancelled) => {
            if (cancelled && snap) {
              onChangeTypes?.(snap.types);
              onChangeSubtypes?.(snap.subs);
            }
            setOpen(false);
          }}
        />
      )}
    </div>
  );
}

/* ---------------- Full list modal (mirrors Sharing UI) ---------------- */

function FullListModal({
  types = [],
  typeIds = [],
  subIds = [],
  onChangeTypes,
  onChangeSubtypes,
  onClose,
}) {
  const typeMap = React.useMemo(
    () => new Map(types.map((t) => [t.id, t])),
    [types]
  );

  // Toggle a parent type; clear its subtypes when turning it off
  const setTypeChecked = (id, checked) => {
    const nextTypes = checked
      ? uniq([...typeIds, id])
      : typeIds.filter((x) => x !== id);
    const rm = (typeMap.get(id)?.subtypes || []).map((s) => s.id);
    const nextSubs = checked
      ? subIds
      : subIds.filter((sid) => !rm.includes(sid));
    onChangeTypes?.(nextTypes);
    onChangeSubtypes?.(nextSubs);
  };

  // “All subtypes” behavior like Sharing
  const setAllSubtypes = (parentId, checked) => {
    const subs = (typeMap.get(parentId)?.subtypes || []).map((s) => s.id);
    if (!subs.length) return;
    const nextSubs = checked
      ? uniq([...subIds, ...subs])
      : subIds.filter((id) => !subs.includes(id));
    if (checked && !typeIds.includes(parentId)) {
      onChangeTypes?.(uniq([...typeIds, parentId]));
    }
    onChangeSubtypes?.(nextSubs);
  };

  // Toggle a single subtype; ensure its parent is on when selecting
  const toggleSubtype = (parentId, subId, checked) => {
    const next = checked
      ? uniq([...subIds, subId])
      : subIds.filter((x) => x !== subId);
    onChangeSubtypes?.(next);
    if (checked && !typeIds.includes(parentId)) {
      onChangeTypes?.(uniq([...typeIds, parentId]));
    }
  };

  return (
    <div className="etx-modal">
      {/* Scoped styles – guarantees nested layout like the Sharing page */}
      <style>{`
        .etx-modal{position:fixed;inset:0;background:rgba(15,18,25,.5);z-index:1000;display:flex;align-items:center;justify-content:center}
        .etx-sheet{width:min(980px,96vw);height:min(86vh,900px);background:#fff;border-radius:14px;display:grid;grid-template-rows:auto 1fr auto;overflow:hidden;box-shadow:0 20px 60px rgba(0,0,0,.2)}
        .etx-header{padding:20px 24px 6px}
        .etx-title{font-weight:700;text-transform:uppercase;letter-spacing:.02em}
        .etx-sub{color:#64748b;font-size:14px;margin-top:6px}
        .etx-body{padding:8px 24px 0;overflow:auto}
        .etx-section{padding:12px 0 10px;border-top:1px solid #eef2f7}
        .etx-row{display:flex;align-items:center;gap:12px;justify-content:space-between}
        .etx-type{display:flex;align-items:center;gap:10px}
        .etx-name{font-weight:800;letter-spacing:.02em;text-transform:uppercase;margin-top:2px}
        .etx-all{display:inline-flex;align-items:center;gap:8px;color:#334155;font-size:13px}
        .etx-subgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(200px,1fr));gap:6px 24px;padding:8px 0 0 2px;margin-left:28px}
        .etx-check{display:inline-flex;align-items:center;gap:8px;white-space:nowrap}
        .etx-footer{display:flex;justify-content:flex-end;gap:10px;padding:12px 16px;background:#fff;border-top:1px solid #eef2f7}
        .etx-btn{padding:8px 14px;border-radius:10px;border:1px solid #e2e8f0;background:#f8fafc;cursor:pointer}
        .etx-btn.primary{background:#0f265e;color:#fff;border-color:#0f265e}
        .etx-btn.ghost{background:#fff}
      `}</style>

      <div className="etx-sheet" role="dialog" aria-modal="true">
        <div className="etx-header">
          <div className="etx-title">User Type Filters</div>
          <div className="etx-sub">
            Only show shared entries from the selected types (and subtypes).
            Leave all unchecked to see everything that matches your viewing
            option.
          </div>
        </div>

        <div className="etx-body">
          {types.map((t) => {
            const subs = t.subtypes || [];
            const allSubIds = subs.map((s) => s.id);
            const allSubsSelected =
              allSubIds.length > 0 &&
              allSubIds.every((id) => subIds.includes(id));
            const anySubsSelected =
              allSubIds.some((id) => subIds.includes(id)) && !allSubsSelected;
            const typeChecked = typeIds.includes(t.id);

            return (
              <section key={t.id} className="etx-section">
                <div className="etx-row">
                  <label className="etx-type">
                    <input
                      type="checkbox"
                      checked={typeChecked}
                      onChange={(e) => setTypeChecked(t.id, e.target.checked)}
                    />
                    <span className="etx-name">{t.name}</span>
                  </label>

                  {!!subs.length && (
                    <label className="etx-all">
                      <IndeterminateCheckbox
                        checked={allSubsSelected}
                        indeterminate={anySubsSelected}
                        onChange={(e) => setAllSubtypes(t.id, e.target.checked)}
                      />
                      <span>All subtypes</span>
                    </label>
                  )}
                </div>

                {!!subs.length && (
                  <div className="etx-subgrid">
                    {subs.map((s) => (
                      <label key={s.id} className="etx-check">
                        <input
                          type="checkbox"
                          checked={subIds.includes(s.id)}
                          onChange={(e) =>
                            toggleSubtype(t.id, s.id, e.target.checked)
                          }
                        />
                        <span>{s.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>

        <div className="etx-footer">
          <button
            type="button"
            className="etx-btn ghost"
            onClick={() => onClose?.(true)}
          >
            Cancel
          </button>
          <button
            type="button"
            className="etx-btn primary"
            onClick={() => onClose?.(false)}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- helpers ---------------- */

const IndeterminateCheckbox = ({ checked, indeterminate, ...props }) => {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate && !checked;
  }, [indeterminate, checked]);
  return <input type="checkbox" ref={ref} checked={!!checked} {...props} />;
};

const uniq = (arr) => Array.from(new Set(arr));

function summarize(allTypes = [], typeIds = [], subIds = []) {
  const names = [];
  const map = new Map(allTypes.map((t) => [t.id, t]));
  typeIds.forEach((id) => {
    const t = map.get(id);
    if (t) names.push(t.name);
  });
  allTypes.forEach((t) =>
    (t.subtypes || []).forEach((st) => {
      if (subIds.includes(st.id)) names.push(`${t.name}: ${st.name}`);
    })
  );
  const MAX = 4;
  const chips = names.slice(0, MAX).map((n, i) => (
    <span key={i} className="chip">
      {n}
    </span>
  ));
  const more = names.length - MAX;
  if (more > 0)
    chips.push(
      <span key="more" className="chip muted">
        +{more} more
      </span>
    );
  if (!names.length)
    chips.push(
      <span key="none" className="muted">
        None selected
      </span>
    );
  return chips;
}
